import type { SearchKeywords } from './search-query-parser';
import { porterStem } from './porter-stemmer';
import { reconstructQuery } from './search-query-parser';

// Field precedence: bureau first, doc_type second, then alphabetical
const FIELD_PRECEDENCE: readonly string[] = ['bureau', 'doc_type'];

/**
 * Result of resolving a parsed search query against known metadata values.
 */
export interface ResolvedSearch {
   /** Field name → matched original-casing values */
   matchedFields: Record<string, string[]>;
   /** Unmatched must terms (kept as "All Metadata" search) */
   remainingMust: string[];
   /** Unmatched OR terms */
   remainingShould: string[];
   /** NOT terms (never matched, always passed through) */
   remainingNot: string[];
   /** Reconstructed search string for the API */
   globalSearchKey: string;
}

/**
 * Build a stemmed index for a list of metadata values.
 * Maps each stemmed word to the set of original values it appears in.
 */
function buildStemIndex(values: string[]): Map<string, Set<string>> {
   const index = new Map<string, Set<string>>();
   for (const value of values) {
      const words = value.split(/\s+/);
      for (const word of words) {
         if (!word)
            continue;
         const stemmed = porterStem(word.toLowerCase());
         let set = index.get(stemmed);
         if (!set) {
            set = new Set();
            index.set(stemmed, set);
         }
         set.add(value);
      }
   }
   return index;
}

/**
 * Resolve a parsed search query against known metadata field values.
 *
 * For each unquoted `must` and `should` term, stems the term and checks
 * against stemmed words in each metadata field's value list. Matched terms
 * are consumed; unmatched terms remain as free-text search.
 *
 * `must_not` terms are never matched — always passed through.
 *
 * @param parsed - Output from `parseUserQuery()`
 * @param metadataFields - Map of field names to their known value lists
 *   e.g. `{ bureau: ["GWQB", "AQB"], doc_type: ["Permit", "Permitting Application"] }`
 *
 * @example
 * resolveSearchQuery(
 *   { must: ['gwqb', 'water'], must_not: [], should: [] },
 *   { bureau: ['GWQB', 'AQB'], doc_type: ['Permit'] }
 * )
 * // → { matchedFields: { bureau: ['GWQB'] }, remainingMust: ['water'], ... }
 */
export function resolveSearchQuery(
   parsed: SearchKeywords,
   metadataFields: Record<string, string[]>,
): ResolvedSearch {
   const matchedFields: Record<string, string[]> = {};
   const remainingMust: string[] = [];
   const remainingShould: string[] = [];

   // Pre-build stem indexes for each field
   const fieldIndexes = new Map<string, Map<string, Set<string>>>();
   for (const [field, values] of Object.entries(metadataFields)) {
      fieldIndexes.set(field, buildStemIndex(values));
   }
   // We only want to apply a term to a single metadata field. Build up a list of possible fields by order of precedence.
   const sortedFieldEntries = [...fieldIndexes.entries()].sort(([a], [b]) => {
      const aIdx = FIELD_PRECEDENCE.indexOf(a);
      const bIdx = FIELD_PRECEDENCE.indexOf(b);
      if (aIdx !== -1 && bIdx !== -1)
         return aIdx - bIdx;
      if (aIdx !== -1)
         return -1;
      if (bIdx !== -1)
         return 1;
      return a.localeCompare(b);
   });

   function matchTerm(term: string): boolean {
      // Quoted terms are exempt from matching
      if (parsed.quoted.has(term)) {
         return false;
      }

      const stemmed = porterStem(term.toLowerCase());

      for (const [field, index] of sortedFieldEntries) {
         const matchedValues = index.get(stemmed);
         if (matchedValues && matchedValues.size > 0) {
            if (!matchedFields[field]) {
               matchedFields[field] = [];
            }
            for (const value of matchedValues) {
               if (!matchedFields[field].includes(value)) {
                  matchedFields[field].push(value);
               }
            }
            return true;
         }
      }

      return false;
   }

   // Process must terms
   for (const term of parsed.must) {
      if (!matchTerm(term)) {
         remainingMust.push(term);
      }
   }

   // Process should terms
   for (const term of parsed.should) {
      if (!matchTerm(term)) {
         remainingShould.push(term);
      }
   }

   // must_not terms are never matched
   const remainingNot = [...parsed.must_not];

   // Reconstruct globalSearchKey from remaining terms, preserving original quotes
   const globalSearchKey = reconstructQuery({
      must: remainingMust,
      should: remainingShould,
      must_not: remainingNot,
      quoted: parsed.quoted,
   });

   return {
      matchedFields,
      remainingMust,
      remainingShould,
      remainingNot,
      globalSearchKey,
   };
}
